import { createRequestActions } from '../core/request';
import { EndpointByMethod } from '../__generated__/access/validators';
import { revalidate } from './cache';
import { getApiClient } from './client';
import type { CacheablePaths } from './cache';
import type { Action } from '@solidjs/router';
import type { FetchOptions, FetchResponse, MaybeOptionalInit } from 'openapi-fetch';
import type { FilterKeys, PathsWithMethod } from 'openapi-typescript-helpers';
import type { paths } from '../__generated__/access/database';

const requestActions = createRequestActions({
	endpointByMethod: EndpointByMethod,
	getApiClient,
	revalidate: revalidate as (key?: unknown, force?: boolean) => Promise<void>,
});

type DeepPartial<T> = T extends object
	? {
			[P in keyof T]?: DeepPartial<T[P]>;
		}
	: T;
type Options<Res extends Record<string, unknown>, Input> = {
	revalidate?:
		| CacheablePaths
		| Array<CacheablePaths>
		| ((data: Res, input: FormData) => Array<CacheablePaths> | undefined);
	redirect?: (data: Res, input: FormData) => undefined | string;
	onSuccess?: (data: Res, input: FormData) => Promise<void>;
	transformer?: (input: FormData) => DeepPartial<Input>;
};

export function clientAction<
	Method extends 'get' | 'patch' | 'post' | 'put' | 'delete',
	Path extends PathsWithMethod<paths, Method>,
	Res extends NonNullable<
		Exclude<
			FetchResponse<Required<paths[Path][Method]>, MaybeOptionalInit<paths[Path], Method>, 'application/json'>['data'],
			'undefined'
		>
	>,
	Rest extends Omit<FetchOptions<FilterKeys<paths[Path], Method>>, 'body'> | undefined,
>(
	method: Method,
	path: Path,
	rest?: Rest | ((data: FormData) => Rest),
	opts?: Options<NonNullable<Res>, FetchOptions<FilterKeys<paths[Path], Method>>['body']>,
	key?: string,
): Action<[FormData], NonNullable<Res>> {
	return requestActions.clientAction(
		method,
		path as string,
		rest as Record<string, unknown> | ((data: FormData) => Record<string, unknown> | undefined) | undefined,
		opts as Parameters<typeof requestActions.clientAction>[3],
		key,
	) as Action<[FormData], NonNullable<Res>>;
}

export function makeRequest<
	Method extends 'get' | 'patch' | 'post' | 'put' | 'delete',
	Path extends PathsWithMethod<paths, Method>,
	Res extends NonNullable<
		Exclude<
			FetchResponse<Required<paths[Path][Method]>, MaybeOptionalInit<paths[Path], Method>, 'application/json'>['data'],
			'undefined'
		>
	>,
	Rest extends Omit<FetchOptions<FilterKeys<paths[Path], Method>>, 'body'> | undefined,
>(
	method: Method,
	path: Path,
	rest?: Rest | ((data: FormData) => Rest),
	opts?: Options<NonNullable<Res>, FetchOptions<FilterKeys<paths[Path], Method>>['body']>,
): (data: FormData) => Promise<Res> {
	return requestActions.makeRequest(
		method,
		path as string,
		rest as Record<string, unknown> | ((data: FormData) => Record<string, unknown> | undefined) | undefined,
		opts as Parameters<typeof requestActions.makeRequest>[3],
	) as (data: FormData) => Promise<Res>;
}
